import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { LogOut, LucideAngularModule, Menu, ShieldCheck, X } from 'lucide-angular';
import { AdminAuthService } from '../core/admin-auth.service';

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, LucideAngularModule],
  template: `
    @if (auth.loggedOut()) {
      <section class="empty-state standalone">
        <span>✓</span><h1>已登出管理後台</h1><p>管理者登入資訊已從此瀏覽器分頁清除。</p>
        @if (auth.localTokenLogin) {
          <form class="local-token-form" (submit)="login(cloudflareToken.value); $event.preventDefault()">
            <label class="field">
              <span>Cloudflare Access token</span>
              <textarea #cloudflareToken rows="6" autocomplete="off" spellcheck="false" placeholder="貼上 token"></textarea>
            </label>
            <button class="button primary full" type="submit" [disabled]="auth.initializing()">{{ auth.initializing() ? '驗證中…' : '重新登入' }}</button>
          </form>
        } @else {
          <button class="button primary" [disabled]="auth.initializing()" (click)="auth.login()">{{ auth.initializing() ? '驗證中…' : '重新登入' }}</button>
        }
        @if (auth.initializationError()) { <p class="form-error">{{ auth.initializationError() }}</p> }
      </section>
    } @else if (auth.initializationError()) {
      <section class="empty-state standalone">
        <span>!</span><h1>無法進入管理後台</h1><p>{{ auth.initializationError() }}</p>
        @if (auth.localTokenLogin) {
          <p>請貼上 Cloudflare Access token，系統會將它送至本機 Admin Session API 換取 Admin token。</p>
          <form class="local-token-form" (submit)="login(cloudflareToken.value); $event.preventDefault()">
            <label class="field">
              <span>Cloudflare Access token</span>
              <textarea #cloudflareToken rows="6" autocomplete="off" spellcheck="false" placeholder="貼上 token"></textarea>
            </label>
            <button class="button primary full" type="submit" [disabled]="auth.initializing()">{{ auth.initializing() ? '驗證中…' : '送出並登入' }}</button>
          </form>
        } @else {
          <button class="button primary" (click)="reload()">重新驗證</button>
        }
      </section>
    } @else if (auth.currentAdmin(); as admin) {
    <div class="admin-shell frontlike-shell">
      <header class="frontlike-header" [class.menu-open]="mobileOpen()">
        <div class="frontlike-header-container">
          <a routerLink="/games" class="frontlike-brand" (click)="mobileOpen.set(false)">
            <img src="assets/logos/crewjam/cj.svg" alt="CrewJam">
            <span class="brand-name">CrewJam</span>
            <span class="brand-divider"></span>
            <span class="admin-label">後台管理</span>
          </a>

          <nav class="frontlike-nav" aria-label="主要導覽">
            <a routerLink="/games" routerLinkActive="active">活動管理</a>
            <a routerLink="/deletion-records" routerLinkActive="active">刪除紀錄</a>
            @if (auth.canManageAdmins()) {
              <a routerLink="/admin-users" routerLinkActive="active">管理者白名單</a>
            }
          </nav>

          <div class="frontlike-account">
            <span class="environment-chip"><i></i>Admin</span>
            <span class="avatar">{{ initials() }}</span>
            <label><span>{{ admin.name }}</span><small>{{ admin.permissions }}</small></label>
            <button class="frontlike-logout-button" aria-label="登出" (click)="auth.logout()"><lucide-angular [img]="LogOut" /></button>
          </div>

          <button class="frontlike-menu-button" aria-label="切換選單" (click)="mobileOpen.set(!mobileOpen())">
            <lucide-angular [img]="mobileOpen() ? X : Menu" />
          </button>
        </div>

        <div class="frontlike-mobile-nav" [class.open]="mobileOpen()">
          <div class="mobile-nav-inner">
            <nav>
              <a routerLink="/games" routerLinkActive="active" (click)="mobileOpen.set(false)">活動管理</a>
              <a routerLink="/deletion-records" routerLinkActive="active" (click)="mobileOpen.set(false)">刪除紀錄</a>
              @if (auth.canManageAdmins()) {
                <a routerLink="/admin-users" routerLinkActive="active" (click)="mobileOpen.set(false)">管理者白名單</a>
              }
            </nav>
            <div class="mobile-admin-card">
              <div><span class="avatar">{{ initials() }}</span><p><strong>{{ admin.name }}</strong><small>{{ admin.email }} · {{ admin.permissions }}</small></p></div>
              <button class="mobile-logout-button" (click)="auth.logout()"><lucide-angular [img]="LogOut" />登出</button>
            </div>
            <div class="mobile-access-note"><lucide-angular [img]="ShieldCheck" /><span>Cloudflare Access 已驗證</span></div>
          </div>
        </div>
      </header>

      <div class="content-shell frontlike-content"><main class="page-container"><router-outlet /></main></div>
    </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminLayoutComponent {
  readonly auth = inject(AdminAuthService);
  readonly mobileOpen = signal(false);
  readonly Menu = Menu;
  readonly X = X;
  readonly ShieldCheck = ShieldCheck;
  readonly LogOut = LogOut;

  initials(): string { return this.auth.currentAdmin()?.name.slice(0, 2) ?? ''; }
  reload(): void { window.location.reload(); }
  login(token: string): void { this.auth.submitLocalToken(token); }
}
